import { Size, Variant, Shape } from "./types";

// 生成 CSS 类名
export const generateClassName = (
  baseClass: string,
  props: {
    variant?: Variant;
    size?: Size;
    shape?: Shape;
    disabled?: boolean;
    loading?: boolean;
    block?: boolean;
    className?: string;
  }
): string => {
  const {
    variant = "primary",
    size = "md",
    shape = "default",
    disabled = false,
    loading = false,
    block = false,
    className = "",
  } = props;

  return [
    baseClass,
    `${baseClass}--${variant}`,
    `${baseClass}--${size}`,
    shape !== "default" && `${baseClass}--${shape}`,
    disabled && `${baseClass}--disabled`,
    loading && `${baseClass}--loading`,
    block && `${baseClass}--block`,
    className,
  ]
    .filter(Boolean)
    .join(" ");
};

// 检查是否为移动端
export const isMobile = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.innerWidth <= 768;
};

// 检查是否支持触摸
export const isTouchDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0;
};

/**
 * 主指针是不是鼠标（即「桌面浏览器」）。
 *
 * 给纯触摸手势（下拉刷新）做开关用：antd-mobile 的 PullToRefresh 底层是
 * @use-gesture 的 drag，**鼠标拖拽同样会触发**。所以在桌面上，只要在页面顶部
 * （scrollTop === 0）用鼠标往下拖过阈值，就会走 onRefresh → window.location.reload()。
 * 开着 DevTools 审查元素时经常在页面里拖来拖去，表现就是「首页一直重复刷新」。
 *
 * 判定刻意保守：只有「有 fine 指针且没有 coarse 指针」才算桌面。任何报了 coarse
 * 的环境（手机、WebView、DevTools 设备模拟）都保留手势，避免移动端误伤丢功能。
 */
export const isMousePrimaryDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    if (typeof window.matchMedia !== "function") return false;
    return (
      window.matchMedia("(pointer: fine)").matches &&
      !window.matchMedia("(pointer: coarse)").matches
    );
  } catch {
    // matchMedia 不可用时按“非桌面”处理：宁可保留手势，也不要在移动端把功能砍掉
    return false;
  }
};

// 合并样式对象
export const mergeStyles = (
  ...styles: (React.CSSProperties | undefined)[]
): React.CSSProperties => {
  return Object.assign({}, ...styles.filter(Boolean));
};

// 生成测试 ID
export const generateTestId = (baseId: string, suffix?: string): string => {
  return suffix ? `${baseId}-${suffix}` : baseId;
};

// 防抖函数
export const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

// 节流函数
export const throttle = <T extends (...args: any[]) => any>(
  func: T,
  limit: number
): ((...args: Parameters<T>) => void) => {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// 检查组件是否应该使用 antd-mobile
export const shouldUseAntdMobile = (): boolean => {
  return isMobile() || isTouchDevice();
};

// 获取组件变体类名
export const getVariantClass = (
  baseClass: string,
  variant: Variant,
  size: Size,
  shape: Shape
): string => {
  return `${baseClass}--${variant}--${size}--${shape}`;
};

// 格式化尺寸值
export const formatSize = (size: Size | number | string): string => {
  if (typeof size === "number") return `${size}px`;
  if (typeof size === "string" && size.includes("px")) return size;
  if (typeof size === "string") return size;

  const sizeMap: Record<Size, string> = {
    xs: "var(--spacing-xs)",
    sm: "var(--spacing-sm)",
    smd: "var(--spacing-smd)",
    md: "var(--spacing-md)",
    lg: "var(--spacing-lg)",
    xl: "var(--spacing-xl)",
  };

  return sizeMap[size as Size] || sizeMap.md;
};
